<template>
  <div class="permission-modules-page">
    <div class="global-header-wrap">
      <div class="user-header-left">
        <div class="left-part">
          <span>{{ $t("user.moduleName") }}</span>
          <div class="listSearch">
            <el-input
              v-model="searchText"
              clearable
              :placeholder="$t('user.moduleSearchPlaceholder')"
              @keyup.enter.native="search"
              @clear="clearSearch"
            />
          </div>
        </div>
        <button class="global-button-style search-btn" @click="search">
          {{ $t("commonMsg.search") }}
        </button>
      </div>
      <div class="right-part">
        <button class="global-button-style add-btn" @click="openDialog()">
          <img src="@/assets/img/cmb_btn_add.png" alt="add" />
          {{ $t("commonMsg.add") }}
        </button>
      </div>
    </div>

    <el-card class="app-table-container" :body-style="{ padding: '20px' }">
      <div slot="header" class="user-table-header">
        <div class="user-table-header-left">
          <p>{{ $t("user.moduleTotal") }}：{{ total }}</p>
          <p class="module-hint">{{ $t("user.moduleHint") }}</p>
        </div>
      </div>
      <div class="global-body-wrap" v-loading="loading">
        <AppTable
          :tableProps="tableProps"
          :tableData="tableData"
          @tableHandle="tableHandle"
        />
      </div>
      <Pagination
        :pageSize="params.limit"
        :total="total"
        :currentPage="currentPage"
        @pageNum="pageNum"
      />
    </el-card>

    <el-dialog
      :title="editId ? $t('user.editModule') : $t('user.addModule')"
      :visible.sync="dialogVisible"
      width="480px"
      :close-on-click-modal="false"
      @close="resetForm"
    >
      <el-form
        ref="moduleForm"
        :model="form"
        :rules="formRules"
        label-width="100px"
      >
        <el-form-item :label="$t('user.moduleName')" prop="name">
          <el-input
            v-model="form.name"
            :placeholder="$t('user.moduleNamePlaceholder')"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">
          {{ $t("commonMsg.cancel") }}
        </el-button>
        <el-button type="primary" @click="submitForm">
          {{ $t("commonMsg.confirm") }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppTable from "@/components/appTable";
import Pagination from "@/components/pagination";
import { isAdmin } from "@/utils/routerPermission";

export default {
  name: "PermissionModules",
  components: {
    AppTable,
    Pagination,
  },
  computed: {
    ...mapGetters(["userInfo"]),
    tableProps() {
      return [
        { prop: "id", label: "ID", width: 80 },
        { prop: "name", label: this.$t("user.moduleName") },
        {
          prop: "handle",
          label: this.$t("commonMsg.handle"),
          width: 140,
          btn: [this.$t("commonMsg.edit"), this.$t("commonMsg.del")],
        },
      ];
    },
    formRules() {
      return {
        name: [
          {
            required: true,
            message: this.$t("user.moduleNameRequired"),
            trigger: "blur",
          },
          { min: 2, message: this.$t("user.moduleNameMin"), trigger: "blur" },
        ],
      };
    },
  },
  data() {
    return {
      loading: false,
      searchText: "",
      tableData: [],
      total: 0,
      currentPage: 1,
      params: { limit: 20, offset: 0 },
      dialogVisible: false,
      editId: null,
      form: { name: "" },
    };
  },
  created() {
    if (!isAdmin(this.userInfo)) {
      this.$router.replace({ name: "errorPage" });
      return;
    }
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const params = {
          limit: this.params.limit,
          offset: (this.currentPage - 1) * this.params.limit,
        };
        const keyword = (this.searchText || "").trim();
        if (keyword) params.text = keyword;
        const res = await this.$http.getModules(params);
        this.tableData = res.data.rows || [];
        this.total = res.data.count || 0;
      } catch (e) {
        this.elMsg(this.$t("alertMsg.fail"), "error");
      } finally {
        this.loading = false;
      }
    },

    search() {
      this.currentPage = 1;
      this.fetchList();
    },

    clearSearch() {
      this.searchText = "";
      this.currentPage = 1;
      this.fetchList();
    },

    pageNum(page) {
      this.currentPage = page;
      this.fetchList();
    },

    openDialog(row) {
      if (row) {
        this.editId = row.id;
        this.form.name = row.name;
      } else {
        this.editId = null;
        this.form.name = "";
      }
      this.dialogVisible = true;
    },

    resetForm() {
      this.editId = null;
      this.form.name = "";
      if (this.$refs.moduleForm) {
        this.$refs.moduleForm.clearValidate();
      }
    },

    submitForm() {
      this.$refs.moduleForm.validate(async (valid) => {
        if (!valid) return;
        try {
          if (this.editId) {
            await this.$http.updatePermissionModule(this.editId, {
              name: this.form.name.trim(),
            });
            this.elMsg(this.$t("alertMsg.modifySuccess"), "success");
          } else {
            await this.$http.addPermissionModule({
              name: this.form.name.trim(),
            });
            this.elMsg(this.$t("alertMsg.addSuccess"), "success");
          }
          this.dialogVisible = false;
          this.fetchList();
        } catch (e) {
          this.elMsg(
            this.editId
              ? this.$t("alertMsg.modifyFailure")
              : this.$t("alertMsg.addFailure"),
            "error"
          );
        }
      });
    },

    tableHandle({ type, data }) {
      const row = data.row;
      if (type === this.$t("commonMsg.edit")) {
        this.openDialog(row);
      } else if (type === this.$t("commonMsg.del")) {
        this.confirmDelete(row);
      }
    },

    confirmDelete(row) {
      this.$confirm(
        this.$t("user.moduleDeleteConfirm"),
        this.$t("commonMsg.tips"),
        {
          confirmButtonText: this.$t("commonMsg.confirm"),
          cancelButtonText: this.$t("commonMsg.cancel"),
          type: "warning",
        }
      )
        .then(() => this.doDelete(row.id))
        .catch(() => {});
    },

    async doDelete(id) {
      try {
        await this.$http.delPermissionModule(id);
        this.elMsg(this.$t("alertMsg.delSuccess"), "success");
        if (this.tableData.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1;
        }
        this.fetchList();
      } catch (e) {
        this.elMsg(this.$t("alertMsg.delFailure"), "error");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.permission-modules-page {
  height: 100%;
  display: flex;
  flex-direction: column;

  /* 与 common.scss 的 flex 头部一致，勿使用 float */
  .global-header-wrap {
    flex-shrink: 0;
  }

  .user-header-left {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 12px;
  }

  .left-part {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;

    > span {
      flex-shrink: 0;
      font-size: 14px;
      white-space: nowrap;
    }

    .listSearch {
      ::v-deep .el-input {
        width: 220px;
      }
    }
  }

  .search-btn,
  .add-btn {
    margin: 0;
    flex-shrink: 0;
  }

  .right-part {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: 16px;
  }

  .user-table-header-left .module-hint {
    margin: 6px 0 0;
    font-size: 12px;
    color: #909399;
    font-weight: normal;
    line-height: 1.5;
  }
}
</style>
